import Enumerable from "linq";
import store from "../../../store";
import { WebStorageAccessor } from "../../../store/WebStorageAccessor";
import { DomainRegistry } from "../../../domain";
import { IRectangle, Rectangle } from "../../../domain/system";
import { IConfiguration, ISystemWindowInitializerConfiguration, ISystemWindowRestorerStoredKeyConfiguration } from "../../../domain/configuration";
import { ILogger, LoggerFactory } from "../../../utilities/logger";
import { AsyncUtility } from "../../../utilities";

export interface IGeometryManageable<T extends IRectangle = IRectangle> {
  getGeometryNames(isWindowRectangleRegisterable: boolean): string[];
  storeGeomerty(source: T, isWindowRectangleRegisterable: boolean): void;
  restoreGeomerty(source: T, isWindowRectangleRegisterable: boolean): void;
}

class WindowRectangleManager implements IGeometryManageable {
  getGeometryNames(isWindowRectangleHandleable: boolean): string[] {
    return [
      isWindowRectangleHandleable ? "ウィンドウ位置/サイズ" : null,
    ];
  }

  storeGeomerty(source: IRectangle, isWindowRectangleHandleable: boolean): void {
    source.x = window.screenX;
    source.y = window.screenY;
    source.width = window.outerWidth;
    source.height = window.outerHeight;
  }

  restoreGeomerty(source: IRectangle, isWindowRectangleHandleable: boolean): void {
    if (!this.isRectangleValid(source)) {
      this.logger.info("位置・サイズの復元をスキップします。");
      return;
    }
    this.relocateWindow(source);
  }

  /**
   * ウィンドウを閉じるときに位置・サイズを保存する設定のとき、
   * 最小化された状態でウィンドウを閉じられると100程度の小さいサイズで保存されてしまっていたので対策している
   */
  private isRectangleValid(source: IRectangle): boolean {
    const configuration = store.getters.configuration as IConfiguration;
    const minSize = configuration.system.window.restorer.restorableMinimumSize;
    return source
        && (minSize.width < source.width)
        && (minSize.height < source.height);
  }

  private async relocateWindow(rectangle: IRectangle): Promise<void> {
    // 読み込み完了前に移動すると移動できない場合があるので待つ
    await this.waitLoadEvent();
    this.logger.debug("ウィンドウ移動開始" + "[" + rectangle.x + "," + rectangle.y + "," + rectangle.width + "," + rectangle.height + "]");
    this.logger.debug("ウィンドウサイズ事前変更開始");
    // Chromeや制限付きのIEではディスプレイ範囲からはみ出るウィンドウの移動や拡大縮小行った場合失敗するのでなるべくリトライしないよう一度縮小する
    await this.setWindowRectangle({
      x: window.screenX,
      y: window.screenY,
      width: Math.min(window.outerWidth, rectangle.width),
      height: Math.min(window.outerHeight, rectangle.height)
    });
    this.logger.debug("ウィンドウの本移動開始");
    // 本移動
    await this.setWindowRectangle(rectangle);
    const additonalRestoreDelayMilliSeconds = this.getConfiguration().system.window.restorer.additonalRestoreDelayMilliSeconds;
    if (additonalRestoreDelayMilliSeconds > 0) {
      this.logger.debug("ウィンドウ移動の遅延実行のためSleepします。");
      await AsyncUtility.sleep(additonalRestoreDelayMilliSeconds);
      this.logger.debug("ウィンドウ移動の遅延実行開始");
      await this.setWindowRectangle(rectangle, 1);  // 最低でも移動とサイズ変更で2回必要なのでリトライ1回を指定している
    }
    this.logger.debug("ウィンドウ移動終了" + "[" + window.screenX + "," + window.screenY + "," + window.outerWidth + "," + window.outerHeight + "]");
  }

  private getConfiguration(): IConfiguration {
    return store.getters.configuration as IConfiguration;
  }

  private async waitLoadEvent(): Promise<void> {
    return new Promise(resolve => {
      if (document.readyState === "complete") {
        resolve();
        return;
      }
      const listener = () => {
        window.removeEventListener("load" , listener);
        resolve();
      };
      window.addEventListener("load" , listener);
    });
  }

  private async setWindowRectangle(rectangle: IRectangle, retryCount: number = 10): Promise<void> {
    return new Promise(resolve => {
      if ((window.screenX === rectangle.x &&
        window.screenY === rectangle.y &&
        window.outerWidth === rectangle.width &&
        window.outerHeight === rectangle.height) ||
        retryCount < 0) {
        resolve();
        return;
      }
      this.logger.debug("ウィンドウ移動中" + "[" + window.screenX + "," + window.screenY + "," + window.outerWidth + "," + window.outerHeight + "][" + retryCount + "]");
      window.moveTo(rectangle.x, rectangle.y);
      window.resizeTo(rectangle.width, rectangle.height);
      setTimeout(async () => {
        await this.setWindowRectangle(rectangle, --retryCount);
        resolve();
      });
    });
  }

  private readonly logger: ILogger = LoggerFactory.getLogger();
}

export class GeometryManager {
  static listenManagers(listener: (names: string[]) => void) {
    this._listener = listener;
    this.notifyManagers();
  }

  private static _listener: (names: string[]) => void;

  private static notifyManagers(): void {
    if (this._listener) {
      this._listener(this.getManagerNames());
    }
  }

  static getManagerNames(): string[] {
    return Enumerable.from(this.managers)
      .selectMany(h => h.getGeometryNames(this.isWindowRectangleRegisterable))
      .where(n => !!n)
      .distinct()
      .toArray();
  }

  static async store(): Promise<void> {
    if (!this.isManageable) {
      return;
    }
    const service = DomainRegistry.developer.getConfigurationRepository();
    await service.registerWindowRectangle(
      store.getters.hostName,
      location.pathname,
      await this.createGeometries());
  }

  private static async createGeometries(): Promise<IRectangle> {
    return Enumerable.from(this.managers)
      .aggregate<IRectangle>(
        await this.loadGeometries(),
        (s, h) => {
          h.storeGeomerty(s, this.isWindowRectangleRegisterable);
          return s;
        });
  }

  static async restore(routePath: string): Promise<void> {
    if (!this.isManageable) {
      return;
    }
    this.restoreGeometries(await this.loadGeometries(routePath));
  }

  private static async loadGeometries(routePath: string = null): Promise<IRectangle> {
    // 遷移する前の画面のpathで位置取得していた不具合があったので対応している
    // GlobalDispatcherに処理があった時代についても対応可能な差分があるので、必要あればそちらを確認すること
    const path = routePath ? routePath : location.pathname;
    if (!this._geometries) {
      const service = DomainRegistry.developer.getConfigurationRepository();
      const geometries = await service.getWindowRectangle(store.getters.hostName, path);
      this._geometries = geometries
        ? geometries
        : this._invalidRectangle; // DBに情報がない場合は無効値で初期化しておいて復元させない.
      this.logger.info("位置情報を取得しました.");
      this.logger.info(JSON.stringify(this._geometries));
      this.overwriteWindowRectangle(path); // window位置は設定によって必要なら上書き.
    }
    return this._geometries;
  }

  private static readonly _invalidRectangle: IRectangle = {
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  };

  private static overwriteWindowRectangle(routePath: string): void {
    const configuration = store.getters.configuration as IConfiguration;
    const restorerConfig = configuration.system.window.restorer;
    if (restorerConfig.isEnable) {
      if (!restorerConfig.shouldStoreDb) { // localstorageから読む設定なので上書きする.
        this.overwriteGeometries(this.loadRectangleFromLocalStorage(routePath)); 
      }
    } else {                              // 復元しない設定なので無効値で上書きして復元させない.
      this.overwriteGeometries(this._invalidRectangle);
    }
    // 復元させないとき、現在のウィンドウのサイズをチェックして必要であれば大きくする
    const initializerConfig = configuration.system.window.initializer;
    if (!this.isSizeSpecified() && this.shouldInitializeWindowSize(initializerConfig)) {
      this.logger.info("横幅もしくは高さが最小の指定値を下回っているので、最小値より大きくなるようにサイズ変更します。");
      const expandedRect: IRectangle = {
        x: initializerConfig.shouldResetLocationWhenResize ? 0 : window.screenX,
        y: initializerConfig.shouldResetLocationWhenResize ? 0 : window.screenY,
        width: Math.max(initializerConfig.minimumSize.width, window.outerWidth),
        height: Math.max(initializerConfig.minimumSize.height, window.outerHeight),
      };
      this.overwriteGeometries(expandedRect);
    }
  }

  private static isSizeSpecified(): boolean {
    return !!this._geometries && !!this._geometries.width && !!this._geometries.height;
  }

  private static shouldInitializeWindowSize(config: ISystemWindowInitializerConfiguration): boolean {
    return config.isEnable
        && !this.hasSameOriginParent()  // Docu3からの起動の場合は親windowから同じサイズを指定されるので初期化対象外とする.
        && ((window.outerWidth < config.minimumSize.width) || (window.outerHeight < config.minimumSize.height));
  }

  private static hasSameOriginParent(): boolean {
    if (!window.opener) {
      this.logger.debug("親windowはありません.");
      return false;
    }
    // 親が別originの場合アクセスした時点でcross-originのDOMExceptionが発生.それを利用して取得できたら同一originと判定.
    // IEは残念ながら以下の同一オリジンポリシー仕様差異によって別origin判定にならないケースあり.
    // (参考: https://developer.mozilla.org/ja/docs/Web/Security/Same-origin_policy)
    // ・双方のドメインが信頼済ゾーンの場合は同一オリジンの制限が適用されない.
    // ・ポート番号だけ違う場合は同一オリジンと見なされる.
    // ↑のケースの場合、IEでは別オリジンと見なされないので、従来動作(window初期化対象外)となるが、許容する.
    // (今後IEの新規案件はなくなっていくと思われるため、がんばっても旨味がない.)
    try {
      const result = !!window.opener.location.href;
      this.logger.debug("親windowは同一originです.");
      return result;
    } catch {
      this.logger.debug("親windowは別originです.");
      return false;
    }
  }

  private static loadRectangleFromLocalStorage(routePath: string): IRectangle {
    const key = this.getStoredKey(routePath);
    return !!key && this.getRectangleFromLocalStorage(key)
      ? this.getRectangleFromLocalStorage(key)
      : this.getRectangleFromLocalStorage(routePath)
        ? this.getRectangleFromLocalStorage(routePath)
        : this._invalidRectangle; // localstorageから値が取れない場合は無効値を返して復元させない.
  }

  static get storedKeys(): ISystemWindowRestorerStoredKeyConfiguration[] {
    return store.getters.configuration.system.window.restorer.storedKeys;
  }

  private static getStoredKey(windowName: string): string {
    const storedKey = Enumerable.from(this.storedKeys ? this.storedKeys : []).where(k => k.route === windowName).select(k => k.key).firstOrDefault();
    return storedKey ? storedKey : "";
  }

  private static getRectangleFromLocalStorage(key: string): Rectangle {
    return (WebStorageAccessor.windowRectangle && WebStorageAccessor.windowRectangle[key])
      ? WebStorageAccessor.windowRectangle[key] as Rectangle
      : null;
  }

  private static overwriteGeometries(source: IRectangle): void {
    if (!source) {
      return;
    }
    this._geometries.x = source.x;
    this._geometries.y = source.y;
    this._geometries.width = source.width;
    this._geometries.height = source.height;
    this.logger.info("位置情報を上書きしました.");
    this.logger.info(JSON.stringify(this._geometries));
  }

  private static _geometries: IRectangle = null;

  private static clearGeometries(): void {
    this._geometries = null;
  }

  private static restoreGeometries(source: IRectangle): void {
    for (const handler of this.managers) {
      handler.restoreGeomerty(source, this.isWindowRectangleRegisterable);
    }
  }

  private static isManageable(): boolean {
    return (0 < this.managers.length);
  }

  private static get managers(): IGeometryManageable[] {
    return this._defaultManagers.concat(this._managers);
  }

  private static _defaultManagers: IGeometryManageable[] = [
    new WindowRectangleManager(),
  ];

  private static get isWindowRectangleRegisterable(): boolean {
    return store.state.configuration
        && store.state.configuration.system.window.restorer.isEnable
        && store.state.configuration.system.window.restorer.shouldStoreDb;
  }

  private static _managers: IGeometryManageable[] = [];

  static async registerManager<T extends IRectangle>(manager: IGeometryManageable<T>): Promise<void> {
    this._managers.push(manager);
    this.notifyManagers();
    manager.restoreGeomerty(await this.loadGeometries() as T, this.isWindowRectangleRegisterable);
  }

  static async unregisterManager<T extends IRectangle>(manager: IGeometryManageable<T>): Promise<void> {
    this._managers = Enumerable.from(this._managers)
      .where(h => h !== manager)
      .toArray();
    this.notifyManagers();
  }

  static unregisterAllManagers(): void {
    this.clearGeometries();
    this._managers = [];
    this.notifyManagers();
  }

  private static readonly logger: ILogger = LoggerFactory.getLogger();
}
